import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/axios';
import { formatChip } from '../utils/formatTime';
import { useSubscription } from '../contexts/SubscriptionContext';

const SUPPORTED_LANGUAGES = ['en', 'es', 'fr', 'hi', 'kn', 'mr', 'ml', 'ta', 'te'];

const LANGUAGE_LABELS = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  hi: 'हिंदी',
  kn: 'ಕನ್ನಡ',
  mr: 'मराठी',
  ml: 'മലയാളം',
  ta: 'தமிழ்',
  te: 'తెలుగు',
};

const JOB_ROLES = [
  'WAITER', 'CHEF', 'SOUS_CHEF', 'KITCHEN_HELPER', 'CAPTAIN',
  'STEWARD', 'CASHIER', 'CLEANER', 'DISHWASHER', 'MANAGER', 'DELIVERY', 'OTHER',
];

const GENDERS = ['MALE', 'FEMALE', 'PREFER_NOT_TO_SAY'];
const TABLE_ROLES = new Set(['WAITER', 'CAPTAIN', 'STEWARD']);
const PHONE_REGEX = /^[6-9]\d{9}$/;
const NAME_PATTERN = /^[A-Za-z][A-Za-z\s.\-']{0,99}$/;
const isNameValid = (value) => value === '' || NAME_PATTERN.test(value);
const EMPLOYEE_CODE_PATTERN = /^[A-Za-z0-9\-_/]*$/;

function getDefaultLanguage(lang) {
  if (!lang) return 'en';
  const base = lang.split('-')[0].toLowerCase();
  return SUPPORTED_LANGUAGES.includes(base) ? base : 'en';
}

function makeEmptyForm(language) {
  return {
    fullName: '',
    phone: '',
    employeeCode: '',
    whatsappLanguage: getDefaultLanguage(language),
    jobRole: '',
    tablesAssigned: '',
    defaultShiftTemplateId: '',
    hourlyRateRupees: '',
    weeklyHourCap: '48',
    active: true,
    temporaryEmployee: false,
    gender: 'MALE',
    dateJoined: new Date().toISOString().slice(0, 10),
    emergencyContactName: '',
    emergencyContactPhone: '',
  };
}

function formatShiftLabel(tpl) {
  const name = tpl.label || tpl.name || tpl.shiftName || 'Shift';
  const chip = (tpl.startTime && tpl.endTime) ? formatChip(tpl.startTime, tpl.endTime) : '';
  return chip ? `${name} — ${chip}` : name;
}

const Spinner = () => (
  <svg className="w-4 h-4 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4z" />
  </svg>
);

function makeFormFromEmployee(emp, language) {
  const phone = emp.phone ? emp.phone.replace(/^\+91/, '') : '';
  const emergencyPhone = emp.emergencyContactPhone
    ? emp.emergencyContactPhone.replace(/^\+91/, '')
    : '';
  return {
    fullName: emp.fullName ?? '',
    phone,
    employeeCode: emp.employeeCode ?? '',
    whatsappLanguage: emp.whatsappLanguage ?? getDefaultLanguage(language),
    jobRole: emp.jobRole ?? '',
    tablesAssigned: emp.tablesAssigned ?? '',
    defaultShiftTemplateId: emp.defaultShiftTemplateId ? String(emp.defaultShiftTemplateId) : '',
    hourlyRateRupees: emp.hourlyRateCost != null ? String(emp.hourlyRateCost / 100) : '',
    weeklyHourCap: emp.weeklyHourCap != null ? String(emp.weeklyHourCap) : '48',
    active: emp.isActive ?? true,
    temporaryEmployee: emp.temporaryEmployee ?? false,
    gender: emp.gender ?? 'MALE',
    dateJoined: emp.dateJoined ?? new Date().toISOString().slice(0, 10),
    emergencyContactName: emp.emergencyContactName ?? '',
    emergencyContactPhone: emergencyPhone,
  };
}

function AddEmployeeModal({ open, onClose, onEmployeeAdded, employee, onEmployeeUpdated }) {
  const isEditMode = !!employee;
  const { t, i18n } = useTranslation('employee');
  const { t: tCommon } = useTranslation('common');
  const { isGated } = useSubscription();
  // Story 17: only Add is gated (per spec's disable list) — edits to existing
  // employees are left alone here.
  const gated = !isEditMode && isGated;
  const [form, setForm] = useState(() =>
    employee ? makeFormFromEmployee(employee, i18n.language) : makeEmptyForm(i18n.language)
  );
  const [touched, setTouched] = useState({});
  const [phoneError, setPhoneError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [savingType, setSavingType] = useState(null);
  const [showMore, setShowMore] = useState(false);
  const [shiftTemplates, setShiftTemplates] = useState([]);
  const fullNameRef = useRef(null);
  const modalRef = useRef(null);
  const isLoading = savingType !== null;

  useEffect(() => {
    if (!open) return;
    if (isEditMode) {
      setForm(makeFormFromEmployee(employee, i18n.language));
    } else {
      setForm(makeEmptyForm(i18n.language));
    }
    setTouched({});
    setPhoneError('');
    setCodeError('');
    setSubmitError('');
    setSavingType(null);
    setShowMore(false);
    apiClient.get('/api/shift-templates')
      .then(res => setShiftTemplates(Array.isArray(res.data) ? res.data : []))
      .catch(() => setShiftTemplates([]));
  }, [open, i18n.language, isEditMode, employee]);

  useEffect(() => {
    if (!open) return;
    const timer = setTimeout(() => fullNameRef.current?.focus(), 50);
    return () => clearTimeout(timer);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, [open]);

  useEffect(() => {
    if (!open || !modalRef.current) return;
    const modal = modalRef.current;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !isLoading) { onClose(); return; }
      if (e.key !== 'Tab') return;
      const focusable = Array.from(
        modal.querySelectorAll('input, select, button, textarea, [tabindex]:not([tabindex="-1"])')
      ).filter(el => !el.disabled);
      if (focusable.length < 2) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault(); last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault(); first.focus();
      }
    };
    modal.addEventListener('keydown', handleKeyDown);
    return () => modal.removeEventListener('keydown', handleKeyDown);
  }, [open, isLoading, showMore, onClose]);

  const handleChange = (e) => {
    let { name, value, type, checked } = e.target;
    if ((name === 'phone' || name === 'emergencyContactPhone') && type !== 'checkbox') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    if (name === 'phone') setPhoneError('');
    if (name === 'employeeCode') setCodeError('');
    setSubmitError('');
  };

  const handleBlur = (e) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const getFormErrors = (f) => {
    const errs = {};
    if (!f.fullName.trim()) errs.fullName = t('addEmployee.validation.fullNameRequired');
    else if (f.fullName.trim().length > 100) errs.fullName = t('addEmployee.validation.fullNameTooLong');
    else if (!isNameValid(f.fullName.trim())) errs.fullName = t('addEmployee.validation.nameInvalidError');
    if (!f.phone) errs.phone = t('addEmployee.validation.phoneRequired');
    else if (!PHONE_REGEX.test(f.phone)) errs.phone = t('addEmployee.validation.phoneInvalid');
    if (!f.whatsappLanguage) errs.whatsappLanguage = t('addEmployee.validation.languageRequired');
    if (!f.jobRole) errs.jobRole = t('addEmployee.validation.jobRoleRequired');
    const rateVal = f.hourlyRateRupees === '' ? NaN : parseFloat(f.hourlyRateRupees);
    if (f.hourlyRateRupees === '') errs.hourlyRateRupees = t('addEmployee.validation.hourlyRateRequired');
    else if (isNaN(rateVal) || rateVal < 0 || rateVal > 10000) errs.hourlyRateRupees = t('addEmployee.validation.hourlyRateInvalid');
    const capVal = f.weeklyHourCap === '' ? NaN : parseInt(f.weeklyHourCap, 10);
    if (f.weeklyHourCap === '') errs.weeklyHourCap = t('addEmployee.validation.weeklyHourCapRequired');
    else if (isNaN(capVal) || capVal < 1 || capVal > 168) errs.weeklyHourCap = t('addEmployee.validation.weeklyHourCapInvalid');
    if (f.emergencyContactPhone && !PHONE_REGEX.test(f.emergencyContactPhone)) {
      errs.emergencyContactPhone = t('addEmployee.validation.phoneInvalid');
    }
    const trimmedCode = f.employeeCode.trim();
    if (trimmedCode) {
      if (trimmedCode.length > 30) errs.employeeCode = t('addEmployee.validation.codeTooLong');
      else if (!EMPLOYEE_CODE_PATTERN.test(trimmedCode)) errs.employeeCode = t('addEmployee.validation.codeInvalid');
    }
    return errs;
  };

  const allErrors = {
    ...getFormErrors(form),
    ...(phoneError ? { phone: phoneError } : {}),
    ...(codeError ? { employeeCode: codeError } : {}),
  };
  const visibleError = (field) => touched[field] ? allErrors[field] : '';

  const prefixedInputClass = (field) =>
    `flex-1 px-3.5 py-2.5 rounded-r-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 disabled:opacity-60 ${
      visibleError(field) ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-orange-500'
    }`;

  const inputClass = (field) =>
    `w-full px-3.5 py-2.5 rounded-lg border bg-white text-gray-900 placeholder-gray-400 outline-none transition focus:ring-2 focus:ring-orange-200 disabled:opacity-60 ${
      visibleError(field) ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-orange-500'
    }`;

  const buildPayload = (f) => ({
    fullName: f.fullName.trim(),
    phone: `+91${f.phone}`,
    employeeCode: f.employeeCode.trim() || null,
    whatsappLanguage: f.whatsappLanguage,
    jobRole: f.jobRole,
    tablesAssigned: f.tablesAssigned.trim() || null,
    hourlyRateCost: Math.round(parseFloat(f.hourlyRateRupees) * 100),
    weeklyHourCap: parseInt(f.weeklyHourCap, 10),
    defaultShiftTemplateId: f.defaultShiftTemplateId ? parseInt(f.defaultShiftTemplateId, 10) : null,
    clearDefaultShiftTemplate: !f.defaultShiftTemplateId,
    active: f.active,
    temporaryEmployee: f.temporaryEmployee,
    gender: f.gender || null,
    dateJoined: f.dateJoined,
    emergencyContactName: f.emergencyContactName.trim() || null,
    emergencyContactPhone: f.emergencyContactPhone ? `+91${f.emergencyContactPhone}` : null,
  });

  const doSubmit = async (keepOpen) => {
    const allTouched = {};
    Object.keys(form).forEach(k => { allTouched[k] = true; });
    setTouched(allTouched);

    const validationErrors = {
      ...getFormErrors(form),
      ...(phoneError ? { phone: phoneError } : {}),
      ...(codeError ? { employeeCode: codeError } : {}),
    };
    if (Object.keys(validationErrors).length > 0) return;

    setSavingType(keepOpen ? 'addAnother' : 'save');
    setPhoneError('');
    setCodeError('');
    setSubmitError('');

    try {
      if (isEditMode) {
        const res = await apiClient.patch(`/api/employees/${employee.id}`, buildPayload(form));
        onEmployeeUpdated?.(res.data);
        onClose();
      } else {
        const res = await apiClient.post('/api/employees', buildPayload(form));
        const name = res.data?.fullName || form.fullName.trim();
        onEmployeeAdded(name);
        if (keepOpen) {
          setForm(makeEmptyForm(i18n.language));
          setTouched({});
          setPhoneError('');
          setCodeError('');
          setShowMore(false);
          setTimeout(() => fullNameRef.current?.focus(), 50);
        } else {
          onClose();
        }
      }
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.messageKey === 'error.employee.phone.exists') {
        setPhoneError(err.response.data.message || t('addEmployee.validation.phoneDuplicate'));
        setTouched(prev => ({ ...prev, phone: true }));
      } else if (err.response?.status === 409 && err.response.data?.messageKey === 'error.employee.code.exists') {
        setCodeError(err.response.data.message || t('addEmployee.validation.codeDuplicate'));
        setTouched(prev => ({ ...prev, employeeCode: true }));
      } else if (err.response?.status === 403) {
        setSubmitError(t('addEmployee.validation.permissionDenied'));
      } else {
        setSubmitError(err.response?.data?.message || t('addEmployee.validation.saveFailed'));
      }
    } finally {
      setSavingType(null);
    }
  };

  if (!open) return null;

  const showTablesField = TABLE_ROLES.has(form.jobRole);

  return (
    <div
      className="fixed inset-0 z-50 flex bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-employee-title"
    >
      <div
        ref={modalRef}
        className="relative w-full h-full sm:h-auto sm:max-w-lg sm:m-auto bg-white sm:rounded-2xl shadow-2xl border border-orange-100 flex flex-col overflow-hidden sm:max-h-[90vh]"
      >
        {/* Header */}
        <div className="flex-shrink-0 flex items-start justify-between px-5 sm:px-7 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 id="add-employee-title" className="text-lg font-semibold text-gray-900">
              {isEditMode ? 'Edit Employee' : t('addEmployee.title')}
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">{t('addEmployee.subtitle')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isLoading}
            className="ml-4 mt-0.5 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
            aria-label={t('modal.close')}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Scrollable form body */}
        <form
          noValidate
          onSubmit={(e) => { e.preventDefault(); doSubmit(false); }}
          className="flex-1 flex flex-col overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-5 sm:px-7 py-5 space-y-7">

            {/* Subscription-gated notice — Add only */}
            {gated && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                </svg>
                <span>{tCommon('gatedAction.tooltip')}</span>
              </div>
            )}

            {/* Submit error banner */}
            {submitError && (
              <div role="alert" className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
                </svg>
                <span>{submitError}</span>
              </div>
            )}

            {/* BLOCK 1 — Who is this person? */}
            <section aria-labelledby="block-identity">
              <h3 id="block-identity" className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('addEmployee.blocks.identity')}
              </h3>
              <div className="space-y-4">

                <div>
                  <label htmlFor="ae-fullName" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.fullName')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    ref={fullNameRef}
                    id="ae-fullName"
                    name="fullName"
                    type="text"
                    autoComplete="name"
                    maxLength={100}
                    value={form.fullName}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('addEmployee.fields.fullNamePlaceholder')}
                    disabled={isLoading}
                    className={inputClass('fullName')}
                  />
                  <p className="mt-1 text-xs text-gray-500">{t('addEmployee.fields.nameHelper')}</p>
                  {visibleError('fullName') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('fullName')}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="ae-gender" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.gender')}
                  </label>
                  <select
                    id="ae-gender"
                    name="gender"
                    value={form.gender}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={inputClass('gender')}
                  >
                    {GENDERS.map(g => (
                      <option key={g} value={g}>{t(`addEmployee.genders.${g}`)}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.genderHelper')}</p>
                </div>

                <div>
                  <label htmlFor="ae-phone" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.phone')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-lg text-sm font-medium select-none">
                      +91
                    </span>
                    <input
                      id="ae-phone"
                      name="phone"
                      type="text"
                      inputMode="numeric"
                      maxLength={10}
                      value={form.phone}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="9876543210"
                      disabled={isLoading}
                      className={prefixedInputClass('phone')}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.phoneHelper')}</p>
                  {visibleError('phone') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('phone')}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="ae-whatsappLanguage" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.whatsappLanguage')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="ae-whatsappLanguage"
                    name="whatsappLanguage"
                    value={form.whatsappLanguage}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={inputClass('whatsappLanguage')}
                  >
                    {SUPPORTED_LANGUAGES.map(lang => (
                      <option key={lang} value={lang}>{LANGUAGE_LABELS[lang]}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.whatsappLanguageHelper')}</p>
                  {visibleError('whatsappLanguage') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('whatsappLanguage')}</p>
                  )}
                </div>
              </div>
            </section>

            {/* BLOCK 2 — What do they do? */}
            <section aria-labelledby="block-role">
              <h3 id="block-role" className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('addEmployee.blocks.role')}
              </h3>
              <div className="space-y-4">

                <div>
                  <label htmlFor="ae-jobRole" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.jobRole')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <select
                    id="ae-jobRole"
                    name="jobRole"
                    value={form.jobRole}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={inputClass('jobRole')}
                  >
                    <option value="">{t('addEmployee.fields.jobRolePlaceholder')}</option>
                    {JOB_ROLES.map(role => (
                      <option key={role} value={role}>{t(`addEmployee.jobRoles.${role}`)}</option>
                    ))}
                  </select>
                  {visibleError('jobRole') && (
                    <p className="mt-1.5 text-xs text-red-600" role="alert">{visibleError('jobRole')}</p>
                  )}
                </div>

                {showTablesField && (
                  <div>
                    <label htmlFor="ae-tablesAssigned" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('addEmployee.fields.tablesAssigned')}
                    </label>
                    <input
                      id="ae-tablesAssigned"
                      name="tablesAssigned"
                      type="text"
                      value={form.tablesAssigned}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder={t('addEmployee.fields.tablesAssignedPlaceholder')}
                      disabled={isLoading}
                      className={inputClass('tablesAssigned')}
                    />
                    <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.tablesAssignedHelper')}</p>
                  </div>
                )}

                <div>
                  <label htmlFor="ae-defaultShift" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.defaultShift')}
                  </label>
                  <select
                    id="ae-defaultShift"
                    name="defaultShiftTemplateId"
                    value={form.defaultShiftTemplateId}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={inputClass('defaultShiftTemplateId')}
                  >
                    <option value="">{t('addEmployee.fields.defaultShiftWillSet')}</option>
                    {shiftTemplates.map(tpl => (
                      <option key={tpl.id} value={tpl.id}>{formatShiftLabel(tpl)}</option>
                    ))}
                  </select>
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.defaultShiftHelper')}</p>
                </div>
              </div>
            </section>

            {/* BLOCK 3 — Pay & hours */}
            <section aria-labelledby="block-pay">
              <h3 id="block-pay" className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('addEmployee.blocks.pay')}
              </h3>
              <div className="space-y-4">

                <div>
                  <label htmlFor="ae-employeeCode" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.employeeCode')}
                  </label>
                  <input
                    id="ae-employeeCode"
                    name="employeeCode"
                    type="text"
                    maxLength={30}
                    value={form.employeeCode}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={t('addEmployee.fields.employeeCodePlaceholder')}
                    disabled={isLoading}
                    className={inputClass('employeeCode')}
                  />
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.employeeCodeHelper')}</p>
                  {visibleError('employeeCode') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('employeeCode')}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="ae-hourlyRate" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.hourlyRate')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-lg text-sm font-medium select-none">
                      ₹
                    </span>
                    <input
                      id="ae-hourlyRate"
                      name="hourlyRateRupees"
                      type="text"
                      inputMode="decimal"
                      value={form.hourlyRateRupees}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="150"
                      disabled={isLoading}
                      className={prefixedInputClass('hourlyRateRupees')}
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.hourlyRateHelper')}</p>
                  {visibleError('hourlyRateRupees') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('hourlyRateRupees')}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="ae-weeklyHourCap" className="block text-sm font-medium text-gray-700 mb-1.5">
                    {t('addEmployee.fields.weeklyHourCap')} <span className="text-red-500" aria-hidden="true">*</span>
                  </label>
                  <input
                    id="ae-weeklyHourCap"
                    name="weeklyHourCap"
                    type="number"
                    min={1}
                    max={168}
                    value={form.weeklyHourCap}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isLoading}
                    className={inputClass('weeklyHourCap')}
                  />
                  <p className="mt-1.5 text-xs text-gray-500">{t('addEmployee.fields.weeklyHourCapHelper')}</p>
                  {visibleError('weeklyHourCap') && (
                    <p className="mt-1 text-xs text-red-600" role="alert">{visibleError('weeklyHourCap')}</p>
                  )}
                </div>
              </div>
            </section>

            {/* BLOCK 4 — Status */}
            <section aria-labelledby="block-status">
              <h3 id="block-status" className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
                {t('addEmployee.blocks.status')}
              </h3>
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    name="active"
                    type="checkbox"
                    checked={form.active}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-orange-500 disabled:opacity-60"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('addEmployee.fields.active')}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{t('addEmployee.fields.activeHelper')}</p>
                  </div>
                </label>
                <label className="flex items-start gap-3 cursor-pointer select-none">
                  <input
                    name="temporaryEmployee"
                    type="checkbox"
                    checked={form.temporaryEmployee}
                    onChange={handleChange}
                    disabled={isLoading}
                    className="mt-0.5 w-4 h-4 rounded border-gray-300 accent-orange-500 disabled:opacity-60"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">{t('addEmployee.fields.temporaryEmployee')}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{t('addEmployee.fields.temporaryEmployeeHelper')}</p>
                  </div>
                </label>
              </div>
            </section>

            {/* BLOCK 5 — More details (optional) */}
            <section>
              <button
                type="button"
                onClick={() => setShowMore(s => !s)}
                className="flex items-center gap-1.5 text-sm font-medium text-orange-600 hover:text-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-200 rounded"
              >
                {showMore ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
                    </svg>
                    {t('addEmployee.buttons.showLess')}
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                    </svg>
                    {t('addEmployee.buttons.showMore')}
                  </>
                )}
              </button>

              {showMore && (
                <div className="mt-4 space-y-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                    {t('addEmployee.blocks.moreDetails')}
                  </h3>

                  <div>
                    <label htmlFor="ae-dateJoined" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('addEmployee.fields.dateJoined')}
                    </label>
                    <input
                      id="ae-dateJoined"
                      name="dateJoined"
                      type="date"
                      value={form.dateJoined}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isLoading}
                      className={inputClass('dateJoined')}
                    />
                  </div>

                  <div>
                    <label htmlFor="ae-emergencyName" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('addEmployee.fields.emergencyContactName')}
                    </label>
                    <input
                      id="ae-emergencyName"
                      name="emergencyContactName"
                      type="text"
                      value={form.emergencyContactName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isLoading}
                      className={inputClass('emergencyContactName')}
                    />
                  </div>

                  <div>
                    <label htmlFor="ae-emergencyPhone" className="block text-sm font-medium text-gray-700 mb-1.5">
                      {t('addEmployee.fields.emergencyContactPhone')}
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center px-3 border border-r-0 border-gray-300 bg-gray-50 text-gray-500 rounded-l-lg text-sm font-medium select-none">
                        +91
                      </span>
                      <input
                        id="ae-emergencyPhone"
                        name="emergencyContactPhone"
                        type="text"
                        inputMode="numeric"
                        maxLength={10}
                        value={form.emergencyContactPhone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        placeholder="9876543210"
                        disabled={isLoading}
                        className={prefixedInputClass('emergencyContactPhone')}
                      />
                    </div>
                    {visibleError('emergencyContactPhone') && (
                      <p className="mt-1.5 text-xs text-red-600" role="alert">{visibleError('emergencyContactPhone')}</p>
                    )}
                  </div>
                </div>
              )}
            </section>

          </div>

          {/* Sticky footer */}
          <div className="flex-shrink-0 border-t border-gray-100 px-5 sm:px-7 py-4 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition disabled:opacity-50"
            >
              {t('addEmployee.buttons.cancel')}
            </button>
            <div className="flex items-center gap-2">
              {!isEditMode && (
                <button
                  type="button"
                  onClick={() => doSubmit(true)}
                  disabled={isLoading || !isNameValid(form.fullName) || gated}
                  title={gated ? tCommon('gatedAction.tooltip') : undefined}
                  className="inline-flex items-center gap-2 rounded-lg border border-orange-500 px-4 py-2 text-sm font-semibold text-orange-600 hover:bg-orange-50 transition disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {savingType === 'addAnother' && <Spinner />}
                  {t('addEmployee.buttons.saveAndAddAnother')}
                </button>
              )}
              <button
                type="submit"
                disabled={isLoading || !isNameValid(form.fullName) || gated}
                title={gated ? tCommon('gatedAction.tooltip') : undefined}
                className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-500 to-amber-600 px-5 py-2 text-sm font-semibold text-white shadow-md shadow-orange-200 transition hover:from-orange-600 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:ring-offset-2 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {savingType === 'save' && <Spinner />}
                {isEditMode ? 'Save changes' : t('addEmployee.buttons.save')}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddEmployeeModal;